/**
 * Canonical PostHog event taxonomy.
 *
 * These constants are the single source of truth for analytics event names
 * used across landing/waitlist (QIN-48), onboarding (WS3), usage meters
 * (WS4), and billing flows.
 * Keep event props to an allowlist (e.g. `goal`, `risk_level`, `resource`,
 * `tier`, `cash_band`, `waitlist_id`, `email_domain`, waitlist form `source`
 * (placement), `form_source`, channel `traffic_source`, `utm_source`,
 * `utm_medium`, `utm_campaign`, `from_channel`, `referrer_host`, `has_ref`)
 * — never include `email`, `name`, referral codes, full referrer
 * URLs, or other PII in event props.
 */
export const ANALYTICS_EVENTS = {
  SIGNUP_COMPLETED: 'signup_completed',
  ONBOARDING_STARTED: 'onboarding_started',
  ONBOARDING_STEP_COMPLETED: 'onboarding_step_completed',
  ONBOARDING_COMPLETED: 'onboarding_completed',
  ONBOARDING_SKIPPED: 'onboarding_skipped',
  FIRST_PORTFOLIO_GENERATED: 'first_portfolio_generated',
  PORTFOLIO_GENERATED: 'portfolio_generated',
  USAGE_LIMIT_REACHED: 'usage_limit_reached',
  UPGRADE_MODAL_VIEWED: 'upgrade_modal_viewed',
  CHECKOUT_STARTED: 'checkout_started',
  SUBSCRIPTION_ACTIVATED: 'subscription_activated',
  /** Scroll-to-#waitlist CTAs. Props: placement (nav | hero | final | decision_grid | pricing). */
  HERO_CTA_CLICKED: 'hero_cta_clicked',
  SAMPLE_CTA_CLICKED: 'sample_cta_clicked',
  WAITLIST_VIEWED: 'waitlist_viewed',
  WAITLIST_SUBMITTED: 'waitlist_submitted',
  WAITLIST_ERROR: 'waitlist_error',
  // Founding offer + referrals (waitlist v2). Props: spots_left (number | null), placement, channel.
  FOUNDING_OFFER_VIEWED: 'founding_offer_viewed',
  REFERRAL_LINK_COPIED: 'referral_link_copied',
  REFERRAL_LINK_SHARED: 'referral_link_shared',
  // Drop quiz (/quiz). Props: level (small | medium | large), capped, founding, ref_used.
  QUIZ_STARTED: 'quiz_started',
  QUIZ_COMPLETED: 'quiz_completed',
  QUIZ_WAITLIST_SUBMITTED: 'quiz_waitlist_submitted',
  /** Landing links to /quiz. Props: placement (hero | waitlist). */
  QUIZ_CTA_CLICKED: 'quiz_cta_clicked',
  /** Landing links to /start plan builder. Props: placement (hero). */
  PLAN_CTA_CLICKED: 'plan_cta_clicked',
  // Guided start flow (/start). Props: step, start_from, amount_band, drop, horizon, goal.
  START_FLOW_VIEWED: 'start_flow_viewed',
  START_FLOW_STEP_COMPLETED: 'start_flow_step_completed',
  START_FLOW_PLAN_BUILT: 'start_flow_plan_built',
  START_FLOW_PLAN_FAILED: 'start_flow_plan_failed',
  START_FLOW_SAVE_CLICKED: 'start_flow_save_clicked',
  START_FLOW_SAVED: 'start_flow_saved',
  // Phase 2 stubs — names locked for GTM; do not block P1 landing work.
  HOLDING_OVERVIEW_OPENED: 'holding_overview_opened',
  PNL_VIEWED: 'pnl_viewed',
} as const;

export type AnalyticsEvent =
  (typeof ANALYTICS_EVENTS)[keyof typeof ANALYTICS_EVENTS];
